import React, { useState } from 'react';
import BulkUpload from './component';
import { getDataFromAPI } from '../../../../HandleAPICalls/actions';
import { connect } from "react-redux";
import Papa from 'papaparse';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const BulkUploadContainer = (props) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [validationStatus, setValidationStatus] = useState('idle');
  const [mappingData, setMappingData] = useState([]);
  const [validationResults, setValidationResults] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isMapping, setIsMapping] = useState(false);
  const [showAlreadyMapped, setShowAlreadyMapped] = useState(true);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [showMappingSuccessDialog, setShowMappingSuccessDialog] = useState(false);
  const [showMissingRecordsDialog, setShowMissingRecordsDialog] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const navigate = useNavigate();

  // API URL
  const VALIDATE_API_URL = '/partner/api/v2/bulk/validate';

  // Parse CSV file
  const parseCSVFile = (file) => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            reject(new Error('CSV parsing error: ' + results.errors[0].message));
          } else {
            resolve(results.data);
          }
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  };

  // Validate CSV data with API using callback pattern
  const validateCSVDataWithAPI = (file) => {
    return new Promise((resolve, reject) => {
      setIsValidating(true);
      setUploadProgress({ current: 0, total: file.size });
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);

      console.log('📤 Uploading file to API...');

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => ({
          ...prev,
          current: Math.min(prev.current + (file.size / 10), file.size)
        }));
      }, 200);

      // Make API call with file upload using callback pattern
      props.getDataFromAPI(
        VALIDATE_API_URL,
        "POST",
        formData,
        (response) => {
          clearInterval(progressInterval);
          setUploadProgress({ current: file.size, total: file.size });
          console.log('✅ Validation API response:', response);
          setIsValidating(false);
          resolve(response);
        },
        (error) => {
          clearInterval(progressInterval);
          console.error('❌ Validation API Network Error:', error);
          const errorMessage = error?.status?.message || 'Network error occurred';
          setIsValidating(false);
          reject(new Error(errorMessage));
        }
      );
    });
  };

  // Process validation results - UPDATED TO HANDLE MISSING RECORDS
  const processValidationResults = (apiResponse, originalData) => {
    console.log('🔄 Processing API response:', apiResponse);
    
    const validEntries = [];
    const invalidEntries = [];
    const alreadyMappedEntries = [];
    const unmappedValidEntries = [];

    // Process already mapped entries
    if (apiResponse?.alreadyMapped) {
      apiResponse.alreadyMapped.forEach((item, index) => {
        const entry = {
          id: `already-${index}`,
          uid: item.uid,
          vmId: item.vmId,
          reason: item.reason,
          isAlreadyMapped: true,
          isInvalid: false
        };
        alreadyMappedEntries.push(entry);
      });
    }

    // Process unmapped valid entries
    if (apiResponse?.unmappedValid) {
      apiResponse.unmappedValid.forEach((item, index) => {
        const entry = {
          id: `unmapped-${index}`,
          uid: item.uid,
          vmId: item.vmId,
          isAlreadyMapped: false,
          isInvalid: false,
          canBeMapped: true
        };
        unmappedValidEntries.push(entry);
        validEntries.push(entry);
      });
    }

    // Process invalid rows
    if (apiResponse?.invalidRows) {
      apiResponse.invalidRows.forEach((item, index) => {
        const entry = {
          id: `invalid-${index}`,
          uid: item.uid,
          vmId: item.vmId,
          status: item.status,
          message: item.message,
          isInvalid: true,
          rowNumber: item.rowNumber
        };
        invalidEntries.push(entry);
      });
    }

    const results = {
      validEntries,
      invalidEntries,
      alreadyMappedEntries,
      unmappedValidEntries,
      totalEntries: apiResponse?.total || originalData.length,
      validCount: apiResponse?.validCount || validEntries.length,
      invalidCount: apiResponse?.invalidCount || invalidEntries.length,
      alreadyMappedCount: apiResponse?.alreadyMapped?.length || 0,
      unmappedValidCount: apiResponse?.unmappedValid?.length || 0,
      canProceed: apiResponse?.canProceed || false,
      missingRecords: apiResponse?.missingRecords || [],
      apiData: apiResponse
    };

    console.log('📊 Updated validation results:', results);
    return results;
  };

  // File upload handler
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    try {
      console.log('📁 Processing file:', file.name);
      
      setUploadedFile({
        name: file.name,
        size: (file.size / (1024 * 1024)).toFixed(1) + 'MB',
        file: file,
        originalSize: file.size
      });

      setCurrentStep(2);
      setValidationStatus('idle');
      setUploadProgress({ current: 0, total: file.size });

      // Parse CSV
      const parsedData = await parseCSVFile(file);
      setCsvData(parsedData);
      
      console.log('📈 Parsed CSV data:', parsedData);
      
      if (parsedData.length === 0) {
        toast.error('CSV file is empty');
        setValidationStatus('idle');
        setCurrentStep(1);
        return;
      }

      // Validate CSV structure
      const firstRow = parsedData[0];
      if (!firstRow.uid || !firstRow.vmId) {
        toast.error('CSV file must contain "uid" and "vmId" columns');
        setValidationStatus('error');
        return;
      }

      // Show validate button
      setValidationStatus('ready');
      console.log('✅ File ready for validation');

    } catch (error) {
      console.error('❌ File processing error:', error);
      toast.error('Failed to process CSV file: ' + error.message);
      setValidationStatus('error');
      setCurrentStep(1);
    }
  };

  // Handle validation button click
  const handleValidate = () => {
    if (!uploadedFile || !uploadedFile.file) {
      toast.error('No file to validate');
      return;
    }

    setValidationStatus('progress');

    // Call validate API with file upload using callback pattern
    validateCSVDataWithAPI(uploadedFile.file)
      .then((apiResponse) => {
        // Process the response with the updated function
        const validationResults = processValidationResults(apiResponse, csvData);
        setValidationResults(validationResults);

        // Check if there are missing records
        if (validationResults.missingRecords && validationResults.missingRecords.length > 0) {
          // Show missing records dialog instead of proceeding
          setShowMissingRecordsDialog(true);
          setValidationStatus('success');
          return;
        }

        // Set initial mapping data based on canProceed
        if (validationResults.canProceed) {
          // If can proceed, show all valid entries
          setMappingData([...validationResults.unmappedValidEntries]);
          setShowAlreadyMapped(false);
        } else {
          // If cannot proceed, show only already mapped entries initially
          setMappingData([...validationResults.alreadyMappedEntries]);
          setShowAlreadyMapped(true);
        }

        setValidationStatus('success');
        setShowValidationDialog(true);
        
      })
      .catch((error) => {
        console.error('❌ Validation process error:', error);
        toast.error(error.message || 'Validation failed. Please check your CSV and try again.');
        setValidationStatus('error');
      });
  };

  // Handle drag and drop
  const handleDrop = async (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      const inputEvent = {
        target: {
          files: [file]
        }
      };
      await handleFileUpload(inputEvent);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  // Remove entry from mapping data
  const handleRemoveEntry = (entryId) => {
    setMappingData(prev => {
      const newData = prev.filter(entry => entry.id !== entryId);
      
      // Check if all already mapped entries are removed
      if (showAlreadyMapped && validationResults) {
        const remainingAlreadyMapped = newData.filter(entry => entry.isAlreadyMapped);
        if (remainingAlreadyMapped.length === 0 && validationResults.unmappedValidCount > 0) {
          // Switch to showing unmapped valid entries
          setMappingData([...validationResults.unmappedValidEntries]);
          setShowAlreadyMapped(false);
          toast.info('All already mapped entries removed. Showing valid entries for mapping.');
        } else if (remainingAlreadyMapped.length === 0 && validationResults.unmappedValidCount === 0) {
          // No unmapped entries available
          setMappingData([]);
          setShowAlreadyMapped(false);
          toast.info('All entries removed. No valid entries available for mapping.');
        } else {
          toast.success('Entry removed successfully');
        }
      }
      
      return newData;
    });
  };

  // Remove all already mapped entries
  const handleRemoveAllAlreadyMapped = () => {
    if (validationResults && validationResults.unmappedValidCount > 0) {
      setMappingData([...validationResults.unmappedValidEntries]);
      setShowAlreadyMapped(false);
      toast.info('All already mapped entries removed. Showing valid entries for mapping.');
    } else {
      setMappingData([]);
      setShowAlreadyMapped(false);
      toast.info('All entries removed. No valid entries available for mapping.');
    }
  };

  // Handle try again - reset to step 1
  const handleTryAgain = () => {
    handleReset();
  };

  // Map entries using callback pattern
  const handleMap = () => {
    if (mappingData.length === 0) {
      toast.error("No valid entries to map");
      return;
    }

    // Only allow mapping if we're showing unmapped valid entries
    if (showAlreadyMapped) {
      toast.error("Please remove already mapped entries first to proceed with mapping");
      return;
    }

    setIsMapping(true);

    // Prepare mapping payload with separate arrays for uids and vmIds
    const mappingPayload = {
      uids: mappingData.map(entry => entry.uid),
      vmIds: mappingData.map(entry => entry.vmId)
    };

    console.log('📤 Sending mapping payload:', mappingPayload);

    // Call mapping API using callback pattern
    return new Promise((resolve, reject) => {
      props.getDataFromAPI(
        '/partner/api/v2/uid-vm/map',
        "POST",
        mappingPayload,
        (response) => {
          console.log("Map API Response:", response);
          setIsMapping(false);
           const successMessage = response.message || `Successfully processed ${mappingData.length} UID-VM mappings!`;
            toast.success(successMessage);
            setShowMappingSuccessDialog(true); // Show the success dialog
            resolve(response);
            navigate(-1)
        },
        (error) => {
          console.error("Map API Network Error:", error);
          const errorMessage = error?.status?.message || 'Network error occurred';
          setIsMapping(false);
          toast.error(errorMessage);
          reject(new Error(errorMessage));
        }
      );
    });
  };

  // Handle validation dialog OK button
  const handleValidationDialogOk = () => {
    setShowValidationDialog(false);
    setCurrentStep(3);
  };

  // Handle missing records dialog OK button
  const handleMissingRecordsDialogOk = () => {
    setShowMissingRecordsDialog(false);
    // Don't proceed to step 3, stay on step 2
    setValidationStatus('ready');
  };

  // Handle mapping success dialog OK button
  const handleMappingSuccessDialogOk = () => {
    setShowMappingSuccessDialog(false);
    navigate(-1); // Navigate back to previous page
  };

  // Reset the entire process
  const handleReset = () => {
    setCurrentStep(1);
    setUploadedFile(null);
    setValidationStatus('idle');
    setMappingData([]);
    setValidationResults(null);
    setCsvData([]);
    setIsValidating(false);
    setIsMapping(false);
    setShowAlreadyMapped(true);
    setShowValidationDialog(false);
    setShowMappingSuccessDialog(false);
    setShowMissingRecordsDialog(false);
    setUploadProgress({ current: 0, total: 0 });
  };

  // Get file upload progress percentage
  const getUploadProgressPercentage = () => {
    if (uploadProgress.total === 0) return 0;
    return Math.round((uploadProgress.current / uploadProgress.total) * 100);
  };

  // Get upload progress text
  const getUploadProgressText = () => {
    const currentMB = (uploadProgress.current / (1024 * 1024)).toFixed(1);
    const totalMB = (uploadProgress.total / (1024 * 1024)).toFixed(1);
    return `${currentMB}MB/${totalMB}MB`;
  };

  return (
    <BulkUpload
      // State props
      currentStep={currentStep}
      uploadedFile={uploadedFile}
      validationStatus={validationStatus}
      mappingData={mappingData}
      validationResults={validationResults}
      isValidating={isValidating}
      isMapping={isMapping}
      showAlreadyMapped={showAlreadyMapped}
      showValidationDialog={showValidationDialog}
      showMappingSuccessDialog={showMappingSuccessDialog}
      showMissingRecordsDialog={showMissingRecordsDialog}
      uploadProgress={uploadProgress}
      
      // Action props
      onFileUpload={handleFileUpload}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onValidate={handleValidate}
      onRemoveEntry={handleRemoveEntry}
      onRemoveAllAlreadyMapped={handleRemoveAllAlreadyMapped}
      onMap={handleMap}
      onReset={handleReset}
      onTryAgain={handleTryAgain}
      setCurrentStep={setCurrentStep}
      onValidationDialogOk={handleValidationDialogOk}
      onMappingSuccessDialogOk={handleMappingSuccessDialogOk}
      onMissingRecordsDialogOk={handleMissingRecordsDialogOk}
      
      // Utility props
      getUploadProgressPercentage={getUploadProgressPercentage}
      getUploadProgressText={getUploadProgressText}
    />
  );
};

export default connect(null, {
  getDataFromAPI,
})(BulkUploadContainer);